/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Input from '../../components/form-control/Input';
import Button from '../../components/form-control/Button';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { completeAccount } from '../../api/requests';
import { ICompleteAccountDTO } from '../../resources/interfaces';
import useUserAccount from '../../hooks/userAccountHook';
import { SIGN_IN } from '../../resources/links';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import { getFormattedPhoneNumber, getPhoneNumberFromShortCodeFormat, phoneValidationSchema, showToast } from '../../utils/miscFunctions';
import useModal from '../../hooks/modalHook';
import SuccessConfirmation from '../../components/elements/SuccessConfirmation';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from '../../assets/images/Brolly Logo.svg';
import { IoLockClosedOutline } from 'react-icons/io5';

function CompleteAccount() {
  const [searchParams] = useSearchParams();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const { updateAccount, signOut } = useUserAccount();
  const navigate = useNavigate();

  const { updateModalStates, ModalComponent, closeModal } = useModal();

  const userEmail = decodeURIComponent(String(searchParams.get('ue')));
  const token = searchParams.get('hc');
  const firstName = searchParams.get('firstname');
  const lastName = searchParams.get('lastname');
  const phone = searchParams.get('phone');

  useEffect(() => {
    signOut(false);
  }, []);

  const submitHandler = async (values: any) => {
    if (userEmail && token) {
      setSubmitting(true);
      const payload: ICompleteAccountDTO = {
        email: userEmail.toLocaleLowerCase(),
        password: values.password,
        refCode: token,
        firstname: values.firstname,
        lastname: values.lastname,
        phone: getFormattedPhoneNumber(values.phone)
      };

      updateAccount({ token: token });

      try {
        const response = await completeAccount(payload);
        if (response) {
          updateModalStates({
            children: (
              <SuccessConfirmation
                onCancel={closeModal}
                title="Successsful"
                description={
                  response?.message ?? 'Your account setup is completed. Please log in to continue'
                }
                onConfirm={() => {
                  updateModalStates({
                    show: false
                  });
                  updateAccount({ token: '' });
                  navigate(SIGN_IN, { replace: true });
                }}
              />
            ),
            size: 'md',
            show: true,
            static: true
          })
        }
      } catch (error: any) {
        // console.log("error", error?.description);
        showToast.error(error?.description);
      } finally {
        setSubmitting(false);
      }}
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 px-5 md:py-10 md:px-16">
      <div>


        <div className="w-full flex items-center justify-center">
          <div>
            <div className="flex justify-center ">
              <img src={Logo} alt="Logo" className="w-24 h-24" />
            </div>

            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-5xl font-bold text-gray-900 tracking-tight leading-tight text-center">
              Complete your account
            </motion.h1>

            <div className="mt-5 max-w-xl">
              <AnimatePresence mode="wait">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100"
                >
                  <Formik
                    initialValues={{
                      password: '',
                      confirmPassword: '',
                      email: userEmail ?? '',
                      firstname: firstName ?? '',
                      lastname: lastName ?? '',
                      // phone: phone ? formatPhoneNumber("+" + phone) : "",
                      phone: getPhoneNumberFromShortCodeFormat(phone ?? '')
                    }}
                    validateOnChange={false}
                    validateOnBlur={true}
                    validationSchema={Yup.object({
                      password: Yup.string()
                        .trim()
                        .required('Password is required')
                        .min(9, 'A minimum of 9 characters required')
                        .test({ message: 'User email cannot be used as password', test: (value) => value !== userEmail })
                        .matches(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/, 'Password must include at least two of the following: an uppercase character, a number, a special character'),
                      confirmPassword: Yup.string().required('Password confirmation is required').oneOf([Yup.ref('password'), null], 'Passwords must match'),
                      email: Yup.string().email('Email is invalid').required('Email address is required.'),
                      phone: phoneValidationSchema()
                    })}
                    onSubmit={(values) => {
                      submitHandler(values);
                    }}>
                    {({ values, errors, handleSubmit, handleChange, handleBlur, setFieldValue, touched }) => (
                      <form
                        className=""
                        onSubmit={handleSubmit}>
                        <div className="">
                          <div className="flex space-x-4 my-5">
                            <Input
                              name="firstname"
                              label="First Name"
                              value={values.firstname}
                              type="text"
                              wrapperClasses=""
                              placeholder="Enter your first name"
                              required
                              error={errors.firstname}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              helperText={errors.firstname}
                              labelClasses='!text-gray-700'
                            />

                            <Input
                              name="lastname"
                              label="Last Name"
                              value={values.lastname}
                              type="text"
                              wrapperClasses=""
                              placeholder="Enter your last name"
                              required
                              error={errors.lastname}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              helperText={errors.lastname}
                              labelClasses='!text-gray-700'
                            />
                          </div>

                          <Input
                            name="email"
                            label="Email address"
                            value={values.email}
                            type="email"
                            wrapperClasses="my-5"
                            required
                            error={errors.email}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            helperText={errors.email}
                            disabled
                            append={<CheckCircleIcon className="w-7 text-[#00c07b] mr-2" />}
                            labelClasses='!text-gray-700'
                          />

                          <Input
                            name="phone"
                            onBlur={handleBlur}
                            onChange={(phone: any) => {
                              setFieldValue('phone', phone.strip());
                            }}
                            value={values.phone}
                            error={errors.phone}
                            helperText={errors.phone}
                            label={'Mobile Phone'}
                            wrapperClasses="w-full"
                            disabled={!errors.phone}
                            append={!errors.phone && <CheckCircleIcon className="w-7 text-[#00c07b] mr-2" />}
                            labelClasses='!text-gray-700'
                            />

                          <Input
                            name="password"
                            label="Password"
                            value={values.password}
                            type="password"
                            wrapperClasses="my-5"
                            showPasswordIcon
                            required
                            prepend={<IoLockClosedOutline className="w-6 h-6" />}
                            error={touched.password ? errors.password : undefined}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            helperText={touched.password ? errors.password : undefined}
                            description={`Must be more than 8 characters. Include an uppercase character, a number and a special character`}
                            labelClasses='!text-gray-700'
                          />
                          {errors.password && (
                            <div className="flex flex-col font-bold text-gray-500 text-sm">
                              <div className="">
                                <span> Password must:</span>
                                <div className="flex items-center gap-2">
                                  <div className="h-2 w-2 bg-gray-500 rounded-full  "></div>{' '}
                                  <span>Be between 9 and 64 characters</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="h-2 w-2 bg-gray-500 rounded-full  "></div>{' '}
                                  <span>Includes at least two of the following:</span>
                                </div>
                              </div>

                              <div className="ml-5 flex flex-col">
                                <span>&#x2022; An uppercase character</span>
                                <span>&#x2022; A number</span>
                                <span>&#x2022; A special character</span>
                              </div>
                            </div>
                          )}

                          <Input
                            name="confirmPassword"
                            label="Confirm Password"
                            value={values.confirmPassword}
                            type="password"
                            wrapperClasses="my-5"
                            showPasswordIcon
                            prepend={<IoLockClosedOutline className="w-6 h-6" />}
                            required
                            error={touched.confirmPassword ? errors.confirmPassword : undefined}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            helperText={touched.confirmPassword ? errors.confirmPassword : undefined}
                            labelClasses='!text-gray-700'
                          />

                          <Button
                            type="submit"
                            label="Submit to Activate Account"
                            bgColor="primary"
                            className="w-full flex justify-center items-center py-3.5 px-4 border border-transparent rounded-lg text-base font-medium text-white bg-gradient-to-r from-gray-800 to-gray-700 hover:from-gray-900 hover:to-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-600 disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-200 shadow-lg shadow-gray-500/20 hover:shadow-gray-500/30"
                            loading={submitting}
                            disabled={submitting}
                          />
                        </div>
                        <div className="text-center mt-2 text-[13px]">
                          Already have an account?{' '}
                          <Link to={SIGN_IN} className="font-semibold ">
                            Sign in
                          </Link>
                        </div>
                      </form>
                    )}
                  </Formik>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
      <ModalComponent />
    </div>
  );
}

export default CompleteAccount;
